import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Inbox, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errText, API } from "@/lib/api";

const BLANK = {
  enabled: false,
  region: "us",
  domain: "",
  diary_address: "",
  todo_address: "",
  postit_address: "",
  allowed_senders: [],
  signing_key_set: false,
};

const STATUS_TONE = {
  processed: "text-emerald-600 dark:text-emerald-400",
  received: "text-muted-foreground",
  rejected: "text-amber-600 dark:text-amber-400",
  failed: "text-destructive",
};

const ADDRESS_FIELDS = [
  ["diary_address", "Diary entries address", "diary"],
  ["todo_address", "To-dos address", "todo"],
  ["postit_address", "Post-its address", "postit"],
];

export const EmailInSettings = () => {
  const [config, setConfig] = useState(BLANK);
  const [key, setKey] = useState("");
  const [sender, setSender] = useState("");
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const webhook = `${API}/inbound/mailgun`;
  const mx = config.region === "eu" ? "mxa.eu.mailgun.org / mxb.eu.mailgun.org" : "mxa.mailgun.org / mxb.mailgun.org";

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([api.get("/inbound/config"), api.get("/inbound/log")]);
      setConfig({ ...BLANK, ...c.data });
      setLog(l.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));

  const save = async (patch = {}) => {
    setSaving(true);
    const next = { ...config, ...patch };
    try {
      const { data } = await api.put("/inbound/config", {
        enabled: next.enabled,
        region: next.region,
        domain: next.domain,
        diary_address: next.diary_address,
        todo_address: next.todo_address,
        postit_address: next.postit_address,
        allowed_senders: next.allowed_senders,
        ...(key.trim() ? { signing_key: key.trim() } : {}),
      });
      setConfig({ ...BLANK, ...data });
      setKey("");
      toast.success("Email-in settings saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const addSender = () => {
    const value = sender.trim().toLowerCase();
    if (!value.includes("@")) return toast.error("Enter a full email address");
    if (config.allowed_senders.includes(value)) return toast.error("Already on the list");
    setSender("");
    save({ allowed_senders: [...config.allowed_senders, value] });
  };

  const fillFromDomain = () => {
    const domain = config.domain.trim().toLowerCase().replace(/^@/, "");
    if (!domain.includes(".")) return toast.error("Enter your receiving domain first");
    set({
      diary_address: `diary@${domain}`,
      todo_address: `todo@${domain}`,
      postit_address: `postit@${domain}`,
    });
    toast.success("Addresses filled in — save when you're happy");
  };

  return (
    <section className="surface p-6 lg:p-8" data-testid="email-in-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
            <Inbox className="h-5 w-5" /> Email in
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Forward or write an email and it lands in the diary: one address for listings, one for
            to-dos, one for post-its. Voice notes attached to the email are transcribed first.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">{config.enabled ? "On" : "Off"}</span>
          <Switch
            data-testid="email-in-enabled-switch"
            checked={config.enabled}
            onCheckedChange={(v) => save({ enabled: v })}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="label-caps">Mailgun region</Label>
          <Select value={config.region} onValueChange={(v) => set({ region: v })}>
            <SelectTrigger data-testid="email-in-region-select" className="rounded-sm h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="us">United States</SelectItem>
              <SelectItem value="eu">Europe</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="label-caps">Receiving domain</Label>
          <div className="flex gap-2">
            <Input
              data-testid="email-in-domain-input"
              value={config.domain}
              onChange={(e) => set({ domain: e.target.value })}
              placeholder="inbound.amir.co.in"
              className="rounded-sm h-11"
            />
            <Button
              variant="outline"
              data-testid="email-in-fill-addresses-btn"
              onClick={fillFromDomain}
              className="rounded-sm h-11 px-4 shrink-0"
            >
              Fill in
            </Button>
          </div>
        </div>

        {ADDRESS_FIELDS.map(([field, label, slug]) => (
          <div key={field} className="space-y-2">
            <Label className="label-caps">{label}</Label>
            <Input
              data-testid={`email-in-${slug}-address`}
              value={config[field]}
              onChange={(e) => set({ [field]: e.target.value })}
              placeholder={`${slug}@inbound.amir.co.in`}
              className="rounded-sm h-11 font-mono text-sm"
            />
          </div>
        ))}

        <div className="space-y-2">
          <Label className="label-caps">
            HTTP webhook signing key {config.signing_key_set ? "· saved" : ""}
          </Label>
          <Input
            type="password"
            data-testid="email-in-signing-key-input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={config.signing_key_set ? "Saved — type to replace" : "Paste from Mailgun → API Security"}
            className="rounded-sm h-11"
          />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <Label className="label-caps">Who may write in</Label>
        <div className="flex flex-wrap gap-2" data-testid="email-in-senders">
          {config.allowed_senders.map((s) => (
            <span
              key={s}
              data-testid={`email-in-sender-${s}`}
              className="inline-flex items-center gap-2 border border-border rounded-full pl-3 pr-1.5 py-1 text-sm"
            >
              {s}
              <button
                data-testid={`email-in-remove-sender-${s}`}
                onClick={() => save({ allowed_senders: config.allowed_senders.filter((x) => x !== s) })}
                className="h-6 w-6 grid place-items-center rounded-full hover:bg-destructive/10 text-destructive transition-colors duration-200"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {config.allowed_senders.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nobody yet — email from an address that is not on this list is ignored.
            </p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="email-in-sender-input"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSender()}
            placeholder="clerk@chambers.in"
            className="rounded-sm h-11 sm:max-w-sm"
          />
          <Button
            variant="outline"
            data-testid="email-in-add-sender-btn"
            onClick={addSender}
            className="rounded-full h-11 px-5"
          >
            <Plus className="h-4 w-4 mr-2" /> Add sender
          </Button>
        </div>
      </div>

      <Button
        data-testid="email-in-save-btn"
        onClick={() => save()}
        disabled={saving}
        className="mt-6 rounded-full h-11 px-6"
      >
        <Check className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
      </Button>

      <div className="mt-8 border border-border rounded-sm p-4 bg-secondary/50">
        <p className="label-caps">Set-up in Mailgun — one time</p>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal pl-5 [&_span]:break-all">
          <li>
            Sign up at mailgun.com (the free plan receives mail; it allows one route, which is enough
            if you use a single catch-all route as in step 4).
          </li>
          <li>
            Add a receiving <strong>subdomain</strong> such as{" "}
            <span className="font-mono">inbound.amir.co.in</span> — never your main mail domain — and
            add the DNS records Mailgun shows, including MX <span className="font-mono">{mx}</span>.
          </li>
          <li>
            Copy the <strong>HTTP webhook signing key</strong> from Mailgun → profile menu → API
            Security, and paste it above.
          </li>
          <li>
            In Mailgun → Receiving → Create route, use expression{" "}
            <span className="font-mono">match_recipient(".*@inbound.amir.co.in")</span>, action{" "}
            <strong>Forward</strong>, destination the webhook URL below.
          </li>
          <li>Add the addresses above, whitelist who may write in, then switch Email in on.</li>
        </ol>
        <div className="mt-4">
          <p className="label-caps mb-2">Webhook URL for the route</p>
          <div className="flex items-center gap-2">
            <code
              data-testid="email-in-webhook-url"
              className="text-xs break-all border border-border rounded-sm px-3 py-2 bg-background"
            >
              {webhook}
            </code>
            <button
              data-testid="email-in-copy-webhook-btn"
              onClick={() => {
                navigator.clipboard?.writeText(webhook);
                toast.success("Webhook URL copied");
              }}
              className="h-9 w-9 shrink-0 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <p className="label-caps">Last emails received</p>
        <div className="mt-2 divide-y divide-border" data-testid="email-in-log">
          {log.map((item) => (
            <div key={item.created_at + item.recipient} className="py-3 text-sm">
              <p className="font-semibold">{item.subject || "(no subject)"}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {item.sender_email} → {item.recipient} · {item.kind}
              </p>
              <p className={`text-xs mt-0.5 ${STATUS_TONE[item.status] || ""}`}>
                {item.status}
                {item.detail ? ` · ${item.detail}` : ""}
              </p>
            </div>
          ))}
          {log.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">
              Nothing yet. Send a test email once the route is live and it will show here.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
};
